import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import {
  ArrowUpRight,
  ChevronRight,
  Scale,
  Gavel,
  Calculator,
  Handshake,
  Check,
  Hammer,
  ShieldAlert,
  FileSearch,
  Flame,
  Users,
  Building,
  BookOpen,
  ClipboardList,
  Eye,
  FilePlus2,
} from "lucide-react";
import { PageHero } from "@/pages/Agence";

const DOMAINS = [
  {
    icon: Hammer,
    title: "Travaux contestés & malfaçons",
    intro: "Vérification de la conformité, respect des DTU et identification des responsabilités.",
    lists: [
      {
        h: "Travaux analysés",
        items: [
          "Ravalement de façade",
          "Réfection de toiture",
          "Étanchéité",
          "Ascenseurs",
          "Chaufferies",
          "Réseaux d'eau & électricité",
          "Ventilation collective",
        ],
      },
      {
        h: "Critères de contrôle",
        items: [
          "Conformité aux DTU",
          "Respect des normes",
          "Qualité d'exécution",
          "Défauts d'exécution",
          "Responsabilités des intervenants",
        ],
      },
    ],
  },
  {
    icon: ShieldAlert,
    title: "Contrôle & audit du syndic",
    intro: "Vérification rigoureuse de la gestion administrative, comptable et juridique.",
    lists: [
      {
        h: "Comptabilité",
        items: [
          "Vérification des comptes",
          "Contrôle des appels de fonds",
          "Analyse des charges",
          "Vérification des provisions",
          "Contrôle des dépenses",
        ],
      },
      {
        h: "Contrats",
        items: [
          "Contrats d'entretien",
          "Contrats de maintenance",
          "Contrats d'assurance",
          "Contrats de travaux",
        ],
      },
      {
        h: "Obligations légales",
        items: [
          "Convocations d'AG",
          "Exécution des résolutions",
          "Carnet d'entretien",
          "Mise en concurrence",
          "Gestion des sinistres",
        ],
      },
    ],
  },
  {
    icon: Calculator,
    title: "Contestation des charges",
    intro: "Détection des erreurs et anomalies dans la répartition des charges.",
    lists: [
      {
        h: "Audit de répartition",
        items: [
          "Vérification de la répartition",
          "Contrôle des tantièmes",
          "Analyse des charges spéciales",
          "Charges ascenseur",
          "Charges chauffage collectif",
          "Détection des erreurs de répartition",
        ],
      },
    ],
  },
  {
    icon: ClipboardList,
    title: "Contestation d'assemblée générale",
    intro: "Analyse technique des décisions votées pouvant faire l'objet d'un recours.",
    lists: [
      {
        h: "Situations courantes",
        items: [
          "Travaux jugés inutiles",
          "Travaux surévalués",
          "Refus de travaux indispensables",
          "Modification de la destination de l'immeuble",
          "Surélévation",
          "Création de nouveaux lots",
          "Transformation des parties communes",
        ],
      },
    ],
  },
  {
    icon: Users,
    title: "Litiges entre copropriétaires",
    intro: "Travaux affectant les parties communes et troubles de voisinage.",
    lists: [
      {
        h: "Travaux affectant les parties communes",
        items: [
          "Ouverture de murs porteurs",
          "Modification des réseaux",
          "Création de vérandas",
          "Fermeture de balcons",
          "Climatisations",
          "Extracteurs de fumées",
        ],
      },
      {
        h: "Troubles de voisinage",
        items: [
          "Nuisances sonores",
          "Vibrations",
          "Odeurs",
          "Activités incompatibles avec le règlement",
        ],
      },
    ],
  },
  {
    icon: Flame,
    title: "Expertise des sinistres",
    intro: "Origine, étendue des dommages, responsabilités et évaluation financière.",
    lists: [
      {
        h: "Dégâts des eaux",
        items: [
          "Origine du sinistre",
          "Responsabilités engagées",
          "Identification des dommages",
          "Travaux nécessaires",
        ],
      },
      {
        h: "Incendies",
        items: [
          "Causes",
          "Étendue des dommages",
          "Responsabilités",
          "Évaluation financière",
        ],
      },
      {
        h: "Catastrophes naturelles",
        items: ["Inondations", "Tempêtes", "Mouvements de terrain", "Sécheresse"],
      },
    ],
  },
  {
    icon: FilePlus2,
    title: "Assistance face aux assurances",
    intro: "Défense de vos intérêts économiques face aux sous-évaluations et refus de garanties.",
    lists: [
      {
        h: "Assureurs concernés",
        items: [
          "Multirisques immeuble",
          "Dommages-ouvrage",
          "Décennale",
          "Responsabilité civile",
        ],
      },
    ],
  },
  {
    icon: Building,
    title: "Copropriétés en difficulté",
    intro: "Audit global à 360° technique, financier et organisationnel.",
    lists: [
      {
        h: "Audit technique",
        items: ["État général du bâti", "Pathologies", "Travaux à prévoir"],
      },
      {
        h: "Audit financier",
        items: ["Analyse comptable", "Trésorerie", "Endettement"],
      },
      {
        h: "Audit organisationnel",
        items: ["Fonctionnement du syndic", "Gouvernance", "Respect réglementaire"],
      },
    ],
  },
];

const PROCEDURE = [
  {
    icon: FileSearch,
    n: "01",
    h: "Analyse du dossier",
  },
  {
    icon: Eye,
    n: "02",
    h: "Constatations sur site",
  },
  {
    icon: Users,
    n: "03",
    h: "Réunions contradictoires",
  },
  {
    icon: BookOpen,
    n: "04",
    h: "Rapport d'expertise",
  },
];

export default function Expertise() {
  const [openDomain, setOpenDomain] = useState(0);
  const domainContentRef = useRef(null);

  const selectDomain = (i) => {
    setOpenDomain(i);
    requestAnimationFrame(() => {
      domainContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div data-testid="page-expertise">
      <PageHero
        overline="Expert judiciaire — Cour d'appel d'Aix-en-Provence"
        title="VOTRE RÉFÉRENCE TECHNIQUE EN COPROPRIÉTÉS ET ASL"
        subtitle="Pour les copropriétaires, conseils syndicaux, colotis, bureaux d’ASL, syndics, avocats. Une expertise indépendante."
      />

      {/* Intro band */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Scale size={36} className="text-[#B8935A] mx-auto" strokeWidth={1.3} />
          <p className="text-[#475569] mt-6 text-lg leading-relaxed text-center">
            Une double compétence au service de la copropriété et de l’ASL.
          </p>
          <p className="text-[#475569] mt-4 text-lg leading-relaxed text-center">
            Fort d’une expérience approfondie de la gestion de copropriété et ASL, notre cabinet propose des missions d’expertise dans deux cadres distincts, adaptés à chaque situation.
          </p>
        </div>
      </section>


      {/* Expertise judiciaire (remplace 'Cadre judiciaire') */}
      <section className="py-16 sm:py-20 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <span className="font-overline-lg text-[#D4B584]">Expertise judiciaire</span>
              <h2 className="font-serif text-4xl mt-4 leading-tight">Expertise judiciaire copropriété et ASL</h2>
              <h3 className="mt-3 text-[#CBD5E1] leading-relaxed">Inscrits sur la liste des experts judiciaire près de la Cour d’Appel d’Aix-en-Provence, nous intervenons sur ordonnance sur requête du Président du tribunal judiciaire dans deux cadres complémentaires.</h3>
            </div>

            <div className="lg:col-span-8 space-y-6">
              <div>
                <h4 className="font-overline text-[#D4B584] text-[12px]">Mission d’administrateur provisoire : Article 29-1</h4>
                <div className="mt-2 text-[#CBD5E1]">
                  <p>L’article 29-1 vise une <strong>crise structurelle de la copropriété</strong> : impayés massifs, dettes importantes, impossibilité d’entretenir l’immeuble, blocages de gestion. Le juge peut alors nommer un administrateur provisoire doté de pouvoirs beaucoup plus larges pour redresser la copropriété.</p>
                </div>
              </div>

              <div>
                <h4 className="font-overline text-[#D4B584] text-[12px]">Mission d’expert : Article 47</h4>
                <div className="mt-2 text-[#CBD5E1]">
                  <p>L’article 47 vise donc une <strong>crise de gouvernance</strong> : il n’y a plus de syndic. L’administrateur provisoire est principalement chargé de récupérer les archives, les comptes et de convoquer une assemblée générale pour désigner un nouveau syndic.</p>
                </div>
              </div>


              <div className="text-[#CBD5E1] text-sm">
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Extra-judicial */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="font-overline-lg text-[#B8935A]">Hors procédures judiciaires</span>
              <h2 className="font-serif text-4xl text-[#0F172A] mt-4 leading-tight">
                Expertise extra-juridictionnelle
              </h2>
              <Handshake size={28} strokeWidth={1.3} className="text-[#B8935A] mt-8" />
            </div>
            <div className="lg:col-span-7">
              <p className="mt-6 text-[#1E293B] leading-relaxed">
                En dehors du cadre judiciaire, nous accompagnons également les copropriétaires, les syndicats de copropriété, les conseils, bureaux d’ASL et les professionnels de l’immobilier dans le cadre d’expertises amiables ou extra-juridictionnelles.
              </p>
              <p className="mt-4 text-[#1E293B] leading-relaxed">
                Ces interventions permettent d’obtenir un avis technique indépendant et objectif, en amont et en dehors de toute procédure judiciaire, pour :
              </p>
              <ul className="mt-6 list-disc list-inside text-[#1E293B] space-y-2">
                <li>Vérifier la régularité de la gestion du syndic.</li>
                <li>Analyser les comptes du syndicat des copropriétaires.</li>
                <li>Contrôler la répartition des charges.</li>
                <li>Examiner la conformité des appels de fonds.</li>
                <li>Vérifier l’exécution des décisions d’assemblée générale.</li>
                <li>Reconstituer une comptabilité défaillante.</li>
                <li>Analyser les causes d’un déficit financier.</li>
                <li>Vérifier le respect des obligations légales du syndic.</li>
                <li>Examiner les contrats conclus par le syndic (maintenance, assurance, travaux, etc.).</li>
                <li>Évaluer les conséquences de l’absence de syndic ou d’une gestion défaillante.</li>
                <li>Auditer la situation administrative et financière d’une copropriété.</li>
                <li>Déterminer si les conditions d’une administration provisoire sont réunies.</li>
                <li>Évaluer les manquements éventuels du syndic en place ou précédent.</li>
                <li>Chiffrer les impayés et apprécier leur impact sur le fonctionnement du syndicat.</li>
                <li>Proposer des mesures de redressement.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why us + double casquette */}
      <section className="py-16 sm:py-20 bg-[#EFE8DC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-overline-lg text-[#B8935A]">Une double casquette</span>
          <h2 className="font-serif text-4xl text-[#0F172A] mt-4 leading-tight">
            Gestionnaire de copropriété <span className="italic">et</span> expert judiciaire.
          </h2>
          <p className="text-[#475569] mt-8 leading-relaxed text-[1.05rem]">
            Nous confère une vision à la fois pratique des problématiques rencontrées. Chaque mission est conduite avec rigueur, impartialité et dans le respect des intérêts de chacune des parties.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-serif text-3xl lg:text-4xl text-[#0F172A]">
            Pour toute demande d’expertise
          </h3>
          <p className="text-[#64748B] mt-5 leading-relaxed">
            Contactez-nous afin d’étudier ensemble la nature et le périmètre de votre besoin.
          </p>
          <Link to="/contact" data-testid="page-expertise-cta" className="gpi-btn-primary mt-10">
            Contactez-nous
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
